<template>
  <Layout>
    <div class="card">
      <div class="card-header">
        <h3 v-if="route.name === 'CreateAPI'" class="text-uppercase">
          {{ $t("t_create_api") }}
        </h3>
        <h3 class="text-uppercase" v-else>{{ $t("t_update_api") }}</h3>
        <div class="text-center">
          <button
            type="button"
            class="btn btn-soft-secondary btn-border mx-3"
            @click="router.go(-1)"
          >
            {{ $t("t-back") }}
          </button>
          <el-button
            type="button"
            style="height: 38px"
            class="btn btn-secondary btn-border"
            @click="createApiDefinitionAsync(ruleFormRef)"
            v-if="route.name === 'CreateAPI'"
            :loading="loadingBtn"
          >
            {{ $t("t-add") }}
          </el-button>

          <el-button
            type="button"
            style="height: 38px"
            class="btn btn-secondary btn-border"
            @click="updateApiDefinitionAsync(ruleFormRef)"
            :loading="loadingBtn"
            v-else
          >
            {{ $t("t-update") }}
          </el-button>
        </div>
      </div>
      <div class="card-body">
        <div class="main-body">
          <el-form
            ref="ruleFormRef"
            :model="dataForm.value"
            status-icon
            label-width="120px"
            label-position="top"
          >
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item
                  :label="$t('t_code_api')"
                  prop="code"
                  :rules="[
                    Validate.required,
                    {
                      pattern: /^[a-zA-Z_][\w-]*$/,
                      message: $t('t_validate_api_code'),
                      trigger: 'change',
                    },
                    {
                      validator: (rule, value, callback) => {
                        if (
                          value &&
                          (/^[\d-]/.test(value) || /-$/.test(value))
                        ) {
                          callback(new Error($t('t_validate_api_code_custom')));
                        } else {
                          callback();
                        }
                      },
                      trigger: 'change',
                    },
                  ]"
                >
                  <el-input
                    type="text"
                    autocomplete="off"
                    size="large"
                    :disabled="route.name === 'UpdateAPI'"
                    v-model="dataForm.value.code"
                    @change="dataForm.value.code = dataForm.value.code.trim()"
                    :placeholder="$t('t_code_api_placeholder')"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item
                  :label="$t('t_type_api')"
                  prop="type"
                  :rules="[Validate.requiredChange]"
                >
                  <el-select
                    v-model="dataForm.value.type"
                    clearable
                    filterable
                    :placeholder="$t('t_type_api_placeholder')"
                    style="width: 100%"
                    size="large"
                  >
                    <el-option
                      v-for="item in typeAPI"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item
                  :label="$t('t_name_api')"
                  prop="name"
                  :rules="[Validate.required]"
                >
                  <el-input
                    type="text"
                    autocomplete="off"
                    size="large"
                    v-model="dataForm.value.name"
                    :placeholder="$t('t_name_api_placeholder')"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item
                  :label="$t('t_description_api')"
                  prop="description"
                >
                  <el-input
                    type="textarea"
                    autocomplete="off"
                    size="large"
                    min="0"
                    :step="1"
                    step-strictly
                    controls-position="right"
                    v-model="dataForm.value.description"
                    :placeholder="$t('t_description_api_placeholder')"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
            </el-row>

            <div
              class="query-api-config"
              @click="showApiConfig = !showApiConfig"
            >
              <h5 class="card-title mb-0">{{ $t("t_query_api_config") }}</h5>
              <i
                class="ri-arrow-up-s-line"
                :class="{ rotateIcon: showApiConfig }"
              ></i>
            </div>
            <div v-if="showApiConfig" class="query-api-config-block">
              <el-row :gutter="20">
                <el-col :span="6">
                  <el-form-item
                    label-width="120px"
                    :label="$t('t_multi_probe')"
                    style="font-weight: 500"
                  >
                    <el-checkbox
                      v-model="dataForm.value.multi_probe"
                      :label="$t('t_multi_probe_note')"
                      size="large"
                      style="font-weight: 400"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item
                    :label="$t('t_config_request_timeout')"
                    prop="config.request_timeout"
                  >
                    <el-input-number
                      type="number"
                      autocomplete="off"
                      size="large"
                      min="1"
                      max="1440"
                      :step="1"
                      step-strictly
                      controls-position="right"
                      v-model="dataForm.value.config.request_timeout"
                      :placeholder="$t('t_config_request_timeout_placeholder')"
                      style="width: 185px"
                    />
                    <span style="margin-left: 10px">{{ $t("t_minutes") }}</span>
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item
                    :label="$t('t_max_request_per_day')"
                    prop="config.max_request_per_day"
                  >
                    <el-input-number
                      autocomplete="off"
                      size="large"
                      min="0"
                      :step="1"
                      step-strictly
                      controls-position="right"
                      v-model="dataForm.value.config.max_request_per_day"
                      :placeholder="$t('t_max_request_per_day_placeholder')"
                      style="width: 185px"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="6">
                  <el-form-item
                    :label="$t('t_max_request_per_user_day')"
                    prop="config.max_request_per_user_day"
                  >
                    <el-input-number
                      autocomplete="off"
                      size="large"
                      min="0"
                      :step="1"
                      step-strictly
                      controls-position="right"
                      v-model="dataForm.value.config.max_request_per_user_day"
                      :placeholder="
                        $t('t_max_request_per_user_day_placeholder')
                      "
                      style="width: 185px"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="24">
                  <el-radio-group
                    v-model="dataForm.value.config.probe_picker_type"
                    @change="dataForm.value.config.probe_column = null"
                    style="
                      display: flex;
                      flex-direction: column;
                      align-items: flex-start;
                    "
                  >
                    <div
                      style="
                        display: flex;
                        gap: 10px;
                        align-items: center;
                        line-height: 100%;
                      "
                    >
                      <el-radio value="automatic">
                        {{ $t("t_probe_picker_type_automatic") }}
                      </el-radio>
                      <el-button
                        type="primary"
                        size="small"
                        @click="addParamDefaultValue()"
                        style="margin-left: 5px"
                        v-if="
                          dataForm.value.config.probe_picker_type ===
                          'automatic'
                        "
                      >
                        <i class="ri-add-line me-1"></i>
                        {{ $t("t_param_default_value") }}
                      </el-button>
                    </div>

                    <div
                      style="
                        display: flex;
                        gap: 10px;
                        align-items: center;
                        line-height: 100%;
                      "
                    >
                      <el-radio value="probe_column">
                        {{ $t("t_probe_picker_type_manual") }}
                      </el-radio>
                      <el-form-item
                        v-if="
                          dataForm.value.config.probe_picker_type ===
                          'probe_column'
                        "
                        style="width: 400px; margin-bottom: 0px"
                      >
                        <el-select
                          v-model="dataForm.value.config.probe_column"
                          :placeholder="$t('t_select_probe_column_code')"
                          style="width: 100%"
                          size="large"
                          @change="dataForm.value.config.probe_column = $event"
                        >
                          <el-option
                            v-for="item in listProbeColumnConfig"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                          />
                        </el-select>
                      </el-form-item>
                    </div>
                  </el-radio-group>
                </el-col>
              </el-row>
            </div>

            <div>
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <h5 class="card-title mb-0">{{ $t("t_parameters") }}</h5>
                <el-button
                  type="primary"
                  size="small"
                  @click="openParameterDialog()"
                >
                  <i class="ri-add-line me-1"></i> {{ $t("t_add_parameter") }}
                </el-button>
              </div>
              <el-table
                :data="dataForm.value.parameters"
                style="width: 100%"
                border
              >
                <el-table-column prop="code" label="Code" />
                <el-table-column prop="dataType" label="DataType">
                  <template #default="scope">
                    <div>
                      {{
                        scope.row.isArray
                          ? "Array of " + scope.row.dataType
                          : scope.row.dataType
                      }}
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="Required" align="center">
                  <template #default="scope">
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 15px;
                      "
                    >
                      <i
                        v-if="scope.row.validation.required"
                        class="ri-checkbox-circle-fill text-success"
                      ></i>
                      <i v-else class="ri-close-circle-fill text-danger"></i>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column
                  label="Length"
                  prop="validation.length"
                  align="center"
                />
                <el-table-column
                  label="Min"
                  prop="validation.min"
                  align="center"
                />
                <el-table-column
                  label="Max"
                  prop="validation.max"
                  align="center"
                />
                <el-table-column label="Pattern" prop="validation.pattern" />
                <el-table-column
                  :label="$t('t-action')"
                  align="center"
                  width="120"
                >
                  <template #default="scope">
                    <el-button
                      type="primary"
                      link
                      size="large"
                      @click="openParameterDialog(scope.row, scope.$index)"
                    >
                      <i class="ri-edit-2-line"></i>
                    </el-button>
                    <el-button
                      type="danger"
                      link
                      size="large"
                      @click="removeParameter(scope.$index)"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- Parameter Dialog -->
            <el-dialog
              v-model="dialogVisible"
              :title="
                isEditMode ? $t('t_edit_parameter') : $t('t_add_parameter')
              "
              width="800px"
              :close-on-click-modal="false"
              align-center
            >
              <el-form
                ref="paramFormRef"
                :model="currentParam"
                label-width="120px"
                label-position="left"
              >
                <el-form-item
                  label="Code"
                  prop="code"
                  :rules="[
                    Validate.required,
                    {
                      pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                      message: $t('t_validate_param_code_pattern'),
                      trigger: 'change',
                    },
                    {
                      validator: (rule, value, callback) => {
                        if (value && /^[0-9]/.test(value)) {
                          callback(
                            new Error($t('t_validate_param_code_start_number'))
                          );
                        } else if (
                          !isEditMode &&
                          dataForm.value.parameters.some(
                            (p) => p.code === value
                          )
                        ) {
                          callback(
                            new Error($t('t_validate_param_code_duplicate'))
                          );
                        } else if (
                          isEditMode &&
                          dataForm.value.parameters.some(
                            (p, idx) => p.code === value && idx !== editingIndex
                          )
                        ) {
                          callback(
                            new Error($t('t_validate_param_code_duplicate'))
                          );
                        } else {
                          callback();
                        }
                      },
                      trigger: 'change',
                    },
                  ]"
                >
                  <el-autocomplete
                    v-model="currentParam.code"
                    :fetch-suggestions="querySearch"
                    :placeholder="$t('t_param_code_placeholder')"
                    clearable
                    class="w-100"
                    trigger-on-focus
                  />
                </el-form-item>
                <el-form-item
                  label="DataType"
                  prop="dataType"
                  :rules="[Validate.requiredChange]"
                >
                  <el-select
                    v-model="currentParam.dataType"
                    :placeholder="$t('t_param_datatype_placeholder')"
                  >
                    <el-option label="String" value="string" />
                    <el-option label="Integer" value="integer" />
                    <el-option label="Long" value="long" />
                    <el-option label="Double" value="double" />
                    <el-option label="Boolean" value="boolean" />
                    <el-option label="Datetime" value="datetime" />
                  </el-select>
                </el-form-item>
                <el-form-item label="Note" prop="note">
                  <el-input
                    type="textarea"
                    v-model="currentParam.note"
                    :placeholder="$t('t_param_note_placeholder')"
                  />
                </el-form-item>
                <el-row :gutter="10">
                  <el-col :span="12">
                    <el-form-item label="IsArray">
                      <el-checkbox v-model="currentParam.isArray" />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item label="Required">
                      <el-checkbox v-model="currentParam.validation.required" />
                    </el-form-item>
                  </el-col>
                </el-row>

                <el-form-item
                  v-if="
                    currentParam.isArray || currentParam.dataType === 'string'
                  "
                  label="Length"
                >
                  <el-input-number
                    v-model="currentParam.validation.length"
                    :min="0"
                    controls-position="right"
                  />
                </el-form-item>
                <el-row :gutter="10">
                  <el-col :span="12">
                    <el-form-item
                      v-if="
                        currentParam.isArray ||
                        currentParam.dataType === 'string'
                      "
                      label="MinLength"
                    >
                      <el-input-number
                        v-model="currentParam.validation.minLength"
                        :min="0"
                        controls-position="right"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item
                      v-if="
                        currentParam.isArray ||
                        currentParam.dataType === 'string'
                      "
                      label="MaxLength"
                    >
                      <el-input-number
                        v-model="currentParam.validation.maxLength"
                        :min="0"
                        controls-position="right"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>

                <template
                  v-if="
                    ['integer', 'long', 'double'].includes(
                      currentParam.dataType
                    )
                  "
                >
                  <el-row :gutter="10">
                    <el-col :span="12">
                      <el-form-item label="Min">
                        <el-input-number
                          v-model="currentParam.validation.min"
                          controls-position="right"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="Max">
                        <el-input-number
                          v-model="currentParam.validation.max"
                          controls-position="right"
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </template>

                <template v-if="currentParam.dataType === 'string'">
                  <el-form-item label="Min">
                    <el-input
                      v-model="currentParam.validation.min"
                      type="number"
                    />
                  </el-form-item>
                  <el-form-item label="Max">
                    <el-input
                      v-model="currentParam.validation.max"
                      type="number"
                    />
                  </el-form-item>
                  <el-form-item label="Pattern">
                    <el-input v-model="currentParam.validation.pattern" />
                  </el-form-item>
                </template>

                <template v-if="currentParam.dataType === 'datetime'">
                  <el-form-item label="Min">
                    <el-date-picker
                      v-model="currentParam.validation.min"
                      type="datetime"
                      value-format="YYYY-MM-DD HH:mm:ss"
                    />
                  </el-form-item>
                  <el-form-item label="Max">
                    <el-date-picker
                      v-model="currentParam.validation.max"
                      type="datetime"
                      value-format="YYYY-MM-DD HH:mm:ss"
                    />
                  </el-form-item>
                </template>
              </el-form>
              <template #footer>
                <span class="dialog-footer">
                  <el-button @click="dialogVisible = false">{{
                    $t("t_cancel")
                  }}</el-button>
                  <el-button type="primary" @click="handleSaveParameter">{{
                    $t("t_save")
                  }}</el-button>
                </span>
              </template>
            </el-dialog>
          </el-form>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import Layout from "../../layouts/main.vue";
import { onMounted, reactive, ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FormInstance } from "element-plus";
import Swal from "sweetalert2";
import MethodService from "../../service/MethodService";
import { useI18n } from "vue-i18n";
import toastr from "toastr";
import DataForm from "./dataQuanLyAPI";
import mushroom from "cem-primary-api";
// import { Filter } from "mushroomjs";
import Validate from "../../service/ValidService";
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const ruleFormRef = ref(FormInstance);
const dataForm = reactive({
  value: MethodService.copyObject(DataForm.dataForm),
});
const typeAPI = DataForm.typeAPI;
// const listSocketGroupAPI = ref([]);
const loadingBtn = ref(false);
const showApiConfig = ref(false);
// const socketGroupApiDefault = reactive({
//   value: "",
//   label: "",
// });
const listProbeColumnConfig = ref([]);
// const fetchSocketGroup = async () => {
//   try {
//     const resSocketGroup = await mushroom.api_socket_group.getAllAsync();
//     const groups = resSocketGroup.result || [];
//     if (groups.length === 0) {
//       listSocketGroupAPI.value = [];
//       return;
//     }
//     const promises = groups.map((group) =>
//       mushroom.api_socket.listAsync({
//         filters: Filter.eq("group_id", group.id).build(),
//         limit: 1,
//       })
//     );
//     const socketsByGroup = await Promise.all(promises);
//     const socketGroupHaveSocket = socketsByGroup
//       .filter((socket) => socket.result?.length > 0)
//       .map((s) => s.result[0].group_id);

//     const filteredGroups = groups.filter((g) =>
//       socketGroupHaveSocket.includes(g.id)
//     );

//     listSocketGroupAPI.value = filteredGroups.map((group) => ({
//       value: group.id,
//       label: group.name,
//     }));
//   } catch (e) {
//     MethodService.showError(e.code);
//   }
// };

const dialogVisible = ref(false);
const isEditMode = ref(false);
const editingIndex = ref(-1);
const paramFormRef = ref(FormInstance);
const currentParam = reactive({
  code: "",
  dataType: "string",
  isArray: false,
  note: "",
  validation: {
    required: false,
    minLength: undefined,
    maxLength: undefined,
    length: undefined,
    min: undefined,
    max: undefined,
    pattern: undefined,
  },
});

const addParamDefaultValue = () => {
  const defaultCodes = ["bras_code", "olt_code", "vlan_id"];
  const currentParams = dataForm.value.parameters || [];
  const others = currentParams.filter((p) => !defaultCodes.includes(p.code));
  const existingDefaults = currentParams.filter((p) =>
    defaultCodes.includes(p.code)
  );
  const finalDefaults = defaultCodes.map((code) => {
    const existing = existingDefaults.find((p) => p.code === code);
    if (existing) {
      return existing;
    }
    return {
      code: code,
      dataType: "string",
      isArray: true,
      note: "",
      validation: {
        required: false,
        minLength: undefined,
        maxLength: undefined,
        length: undefined,
        min: undefined,
        max: undefined,
        pattern: undefined,
      },
    };
  });

  dataForm.value.parameters = [...finalDefaults, ...others];
};

const suggestions = [
  { value: "bras_code" },
  { value: "olt_code" },
  { value: "vlan_id" },
  { value: "start_date" },
  { value: "stop_date" },
];

const querySearch = (queryString, cb) => {
  const results = queryString
    ? suggestions.filter((s) =>
        s.value.toLowerCase().includes(queryString.toLowerCase())
      )
    : suggestions;
  cb(results);
};

const openParameterDialog = (param = null, index = -1) => {
  dialogVisible.value = true;
  if (param) {
    isEditMode.value = true;
    editingIndex.value = index;
    Object.assign(currentParam, JSON.parse(JSON.stringify(param)));
  } else {
    isEditMode.value = false;
    editingIndex.value = -1;
    Object.assign(currentParam, {
      code: "",
      dataType: "string",
      isArray: false,
      note: "",
      validation: {
        required: false,
        minLength: undefined,
        maxLength: undefined,
        length: undefined,
        min: undefined,
        max: undefined,
        pattern: undefined,
      },
    });
  }
  if (paramFormRef.value) paramFormRef.value.clearValidate();
};

const handleSaveParameter = async () => {
  if (!paramFormRef.value) return;
  await paramFormRef.value.validate((valid) => {
    if (valid) {
      if (isEditMode.value) {
        dataForm.value.parameters[editingIndex.value] = JSON.parse(
          JSON.stringify(currentParam)
        );
      } else {
        if (!dataForm.value.parameters) dataForm.value.parameters = [];
        dataForm.value.parameters.push(
          JSON.parse(JSON.stringify(currentParam))
        );
      }
      dialogVisible.value = false;
    }
  });
};

const removeParameter = (index) => {
  Swal.fire({
    title: t("swal_noti"),
    text: t("t_parameter_will_remove"),
    icon: "warning",
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
    showCancelButton: true,
    allowOutsideClick: false,
  }).then((result) => {
    if (result.isConfirmed) {
      dataForm.value.parameters.splice(index, 1);
    }
  });
};

const createApiDefinitionAsync = async (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    loadingBtn.value = true;
    try {
      const response = await mushroom.api_definition.createAsync({
        code: dataForm.value.code,
        name: dataForm.value.name,
        type: dataForm.value.type,
        multi_probe: dataForm.value.multi_probe,
        description: dataForm.value.description,
        parameters: dataForm.value.parameters,
        config: {
          request_timeout: dataForm.value.config.request_timeout,
          max_request_per_day: dataForm.value.config.max_request_per_day,
          max_request_per_user_day:
            dataForm.value.config.max_request_per_user_day,
          probe_picker_type: dataForm.value.config.probe_picker_type,
          probe_column: dataForm.value.config.probe_column,
        },
      });
      if (response.result) {
        toastr.success(t("t_create_api_definition_success"));
        router.go(-1);
      }
    } catch (error) {
      console.error("Có lỗi: %o", error);
      if (error.code === 401) {
        toastr.error(t("t_api_unique_key"));
      } else {
        MethodService.showError(error.code);
      }
    } finally {
      loadingBtn.value = false;
    }
  });
};

const updateApiDefinitionAsync = async (formEl) => {
  if (!formEl) return;
  formEl.validate(async (valid) => {
    if (!valid) return;
    loadingBtn.value = true;
    try {
      const response = await mushroom.api_definition.partialUpdateAsync({
        id: route.params.id,
        name: dataForm.value.name,
        type: dataForm.value.type,
        multi_probe: dataForm.value.multi_probe,
        description: dataForm.value.description,
        parameters: dataForm.value.parameters,
        config: {
          request_timeout: dataForm.value.config.request_timeout,
          max_request_per_day: dataForm.value.config.max_request_per_day,
          max_request_per_user_day:
            dataForm.value.config.max_request_per_user_day,
          probe_picker_type: dataForm.value.config.probe_picker_type,
          probe_column: dataForm.value.config.probe_column,
        },
      });
      if (response.result) {
        toastr.success(t("t_update_api_definition_success"));
        router.go(-1);
      }
    } catch (error) {
      console.error("Có lỗi: %o", error);
      MethodService.showError(error.code);
    } finally {
      loadingBtn.value = false;
    }
  });
};

// const fetchSocketGroupApiDefault = async () => {
//   try {
//     const { result } = await mushroom.system_config.listAsync({
//       filters: Filter.eq("key", "socket_group_id_for_multiple_probes").build(),
//       limit: 1,
//     });
//     console.log("Các system_config: %o", result);
//     socketGroupApiDefault.value = result[0]?.value || "";
//     const socketGroupAPI = listSocketGroupAPI.value.find(
//       (group) => group.value === socketGroupApiDefault.value
//     );
//     socketGroupApiDefault.label = socketGroupAPI?.label || "";
//     console.log("socketGroupAPI: %o", socketGroupApiDefault);
//   } catch (e) {
//     console.error("Có lỗi: %o", e);
//   }
// };

const fetchProbeColumnConfig = async () => {
  const langConfig = localStorage.getItem("language") || "vi";
  try {
    const { result } = await mushroom.probe_column_config.getAllAsync();
    listProbeColumnConfig.value = (result || []).map((item) => ({
      value: item.code,
      label:
        item.code +
        " - " +
        (langConfig === "vi"
          ? item.title_lang?.vi || item.title
          : item.title_lang?.en || item.title),
    }));
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

const fetchApiDefinition = async () => {
  try {
    const response = await mushroom.api_definition.findByIdAsync({
      id: route.params.id,
    });
    if (response.result) {
      dataForm.value = {
        ...response.result,
        parameters: response.result.parameters || [],
        config: response.result.config || {},
      };
    }
  } catch (e) {
    console.error("Có lỗi: %o", e);
  }
};

onMounted(async () => {
  if (route.name === "CreateAPI") {
    dataForm.value.config.probe_picker_type = "automatic";
  }
  await fetchProbeColumnConfig();
  // await fetchSocketGroup();
  // await fetchSocketGroupApiDefault();
  if (route.name === "UpdateAPI") {
    await fetchApiDefinition();
  }
});
</script>
<style scoped lang="scss">
.form-label {
  font-size: 14px;
  padding: 2px 7px;
  border-radius: 5px;
  background-color: #f5f5f5;
  width: fit-content;
}
.query-api-config {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  cursor: pointer;

  i {
    font-size: 20px;
    margin-left: 8px;
    color: #6c757d;
    transition: 0.2s;
  }
  i.rotateIcon {
    transform: rotate(180deg);
  }
}
.query-api-config-block {
  padding: 15px;
  border: 1px solid #e7e7e7;
  border-radius: 5px;
  margin-bottom: 25px;
}
label.el-radio {
  margin-bottom: 0px;
  min-height: 40px;
}
</style>
